import { Button, Modal } from 'react-bootstrap';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaLightbulb,
} from 'react-icons/fa';
import * as S from './styled';

export type ImportError = { line: number; message: string };

export type ImportResult = {
  inserted: number;
  updated: number;
  errors: ImportError[];
};

type Props = {
  result: ImportResult | null;
  onClose: () => void;
};

const UUID_NOT_FOUND = /uuid\s+([0-9a-f-]{8,})\s+n[ãa]o encontrado/i;

export default function ImportResultModal({ result, onClose }: Props) {
  if (!result) return null;

  const { inserted, updated, errors } = result;

  const uuidErrors = errors
    .map((error) => ({ ...error, uuid: error.message?.match(UUID_NOT_FOUND)?.[1] }))
    .filter((error) => error.uuid);
  const otherErrors = errors.filter(
    (error) => !UUID_NOT_FOUND.test(error.message ?? '')
  );

  return (
    <Modal
      show
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      scrollable
    >
      <Modal.Header>
        <Modal.Title style={{ fontSize: '18px' }}>
          Resultado da importação
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <S.ImportSummary>
          <div className="card success">
            <strong>{inserted}</strong>
            <span>produto(s) cadastrado(s)</span>
          </div>
          <div className="card info">
            <strong>{updated}</strong>
            <span>produto(s) atualizado(s)</span>
          </div>
          <div className={`card ${errors.length ? 'danger' : 'success'}`}>
            <strong>{errors.length}</strong>
            <span>linha(s) não importada(s)</span>
          </div>
        </S.ImportSummary>

        {errors.length === 0 && (
          <S.ImportNotice className="success">
            <FaCheckCircle size={22} />
            <div>Todas as linhas da planilha foram importadas.</div>
          </S.ImportNotice>
        )}

        {uuidErrors.length > 0 && (
          <S.ImportSection>
            <S.ImportNotice className="warning">
              <FaExclamationTriangle size={22} />
              <div>
                <strong>
                  {uuidErrors.length} produto(s) não importado(s)
                </strong>
                <p>
                  Essas linhas têm um código na coluna <b>uuid</b> que não
                  existe nesta loja. Por isso o sistema não sabe qual produto
                  atualizar.
                </p>
              </div>
            </S.ImportNotice>

            <S.ImportNotice className="tip">
              <FaLightbulb size={22} />
              <div>
                <strong>Como resolver</strong>
                <ol>
                  <li>Abra a planilha.</li>
                  <li>
                    Nas linhas listadas abaixo, <b>apague o conteúdo da coluna
                    uuid</b> e deixe a célula vazia.
                  </li>
                  <li>Salve e importe a planilha novamente.</li>
                </ol>
                <p>
                  Linha com <b>uuid vazio</b> = produto novo. Linha com uuid
                  preenchido = atualização de um produto que já existe.
                </p>
              </div>
            </S.ImportNotice>

            <S.ImportTable>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Linha</th>
                  <th>uuid que deve ficar vazio</th>
                </tr>
              </thead>
              <tbody>
                {uuidErrors.map((error) => (
                  <tr key={`uuid-${error.line}`}>
                    <td>{error.line}</td>
                    <td className="mono">{error.uuid}</td>
                  </tr>
                ))}
              </tbody>
            </S.ImportTable>
          </S.ImportSection>
        )}

        {otherErrors.length > 0 && (
          <S.ImportSection>
            <S.ImportNotice className="danger">
              <FaExclamationTriangle size={22} />
              <div>
                <strong>
                  {otherErrors.length} linha(s) com outros problemas
                </strong>
                <p>Corrija as linhas abaixo e importe a planilha novamente.</p>
              </div>
            </S.ImportNotice>

            <S.ImportTable>
              <thead>
                <tr>
                  <th style={{ width: '90px' }}>Linha</th>
                  <th>Problema</th>
                </tr>
              </thead>
              <tbody>
                {otherErrors.map((error, index) => (
                  <tr key={`other-${error.line}-${index}`}>
                    <td>{error.line}</td>
                    <td>{error.message}</td>
                  </tr>
                ))}
              </tbody>
            </S.ImportTable>
          </S.ImportSection>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Entendi
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
